import { Fragment } from "react";

const PHASE_LABEL = { listen: "Listen", reveal: "Reveal", reimagine: "Reimagine", guide: "Guide" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

/**
 * Restrained trail of the last three Managing-Principal-APPROVED emphasis
 * events for this engagement. Because every entry in `emphasis_history` is
 * (by contract, upstream) a governed approval, Brass is appropriate here
 * for the dot markers and the section heading — this is the "recognition"
 * case brass exists for. Everything else uses the semantic muted /
 * foreground tokens so the strip reads as calm chrome.
 */
export default function EmphasisHistoryStrip({ history = [] }) {
  const last3 = (history || []).slice(-3);
  if (last3.length === 0) {
    return (
      <div className="text-[0.6875rem] text-muted-foreground border-t border-border/50 pt-4 mt-4 italic" style={{ fontFamily: "Georgia, serif" }}>
        No approved emphasis on record yet. The first Managing-Principal approval will begin the trail.
      </div>
    );
  }

  return (
    <div className="border-t border-border/50 pt-5 mt-5" data-testid="emphasis-history-strip">
      <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-brass mb-3 flex items-baseline gap-2">
        <span>Emphasis trail</span>
        {history.length > 3 && (
          <span
            className="text-muted-foreground normal-case tracking-widest opacity-70"
            data-testid="emphasis-history-earlier-count"
            title={`${history.length - 3} earlier approval${history.length - 3 === 1 ? "" : "s"} not shown`}
          >
            +{history.length - 3}
          </span>
        )}
      </div>
      <div className="flex items-start gap-1 flex-wrap">
        {last3.map((h, idx) => (
          <Fragment key={idx}>
            <div className="group relative" data-testid={`emphasis-history-item-${idx}`}>
              <div className="flex items-center gap-2 border border-border bg-secondary/30 px-3 py-2 hover:border-brass transition-colors cursor-help">
                <span
                  className="inline-block w-2 h-2 rounded-full bg-brass"
                  style={{ boxShadow: "0 0 0 2px hsl(var(--brass) / 0.28)" }}
                />
                <div>
                  <div className="text-sm font-bold text-foreground" style={{ fontFamily: "Georgia, serif" }}>
                    {PHASE_LABEL[h.phase] || h.phase}
                  </div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                    {fmtDate(h.effective_date || h.approved_at)}
                  </div>
                </div>
              </div>
              <div className="hidden group-hover:block absolute z-30 left-0 top-full mt-1 w-72 bg-card border border-border p-4 shadow-xl">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-brass mb-1">Rationale</div>
                <p className="text-xs leading-relaxed text-foreground" style={{ fontFamily: "Georgia, serif" }}>
                  {h.rationale}
                </p>
                <div className="mt-3 pt-2 border-t border-border/50 flex justify-between text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                  <span>{h.source === "yadidai_suggestion" ? "YadidAi + MP approval" : "Managing Principal"}</span>
                  <span className="text-foreground truncate max-w-[10rem]">{h.approved_by_name || h.approved_by}</span>
                </div>
              </div>
            </div>
            {idx < last3.length - 1 && (
              <div className="pt-4">
                <span className="inline-block h-px w-6" style={{ background: "hsl(var(--brass) / 0.35)" }} />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
